/**
 * Hijri Calendar Service
 * 
 * Frontend service layer for hijri date API calls.
 * Replaces the old direct Aladhan API calls.
 */

import { apiService } from './api.service';
import { API_ENDPOINTS } from '@/config/api.config';

// ── Types ─────────────────────────────────────────────────────────────

export interface HijriTodayResponse {
  day: number;
  month: number;
  monthName: string;
  monthNameArabic: string;
  year: number;
  formatted: string;
  adjustment: number;
  gregorian: {
    date: string;
    day: string;
    weekday: string;
    month: number;
    monthName: string;
    year: number;
  };
}

export interface HijriCalendarDay {
  hijriDay: number;
  hijriMonth: number;
  hijriMonthName: string;
  hijriMonthNameArabic: string;
  hijriYear: number;
  gregorianDate: string;
  gregorianDay: number;
  gregorianMonth: number;
  gregorianMonthName: string;
  gregorianYear: number;
  weekday: string;
}

export interface HijriCalendarResponse {
  month: number;
  year: number;
  monthName: string;
  monthNameArabic: string;
  daysInMonth: number;
  adjustment: number;
  days: HijriCalendarDay[];
}

export interface HijriPreference {
  adjustment: number;
  label: string;
  city: string;
  country: string;
}

// ── API calls ─────────────────────────────────────────────────────────

/** Get today's hijri date (adjusted per user preference) */
export async function getHijriToday(): Promise<HijriTodayResponse | null> {
  try {
    return await apiService.get<HijriTodayResponse>(API_ENDPOINTS.HIJRI.TODAY);
  } catch (error) {
    console.error('Failed to fetch hijri today', error);
    return null;
  }
}

/** Get full hijri calendar for a month/year */
export async function getHijriCalendar(
  month?: number,
  year?: number,
): Promise<HijriCalendarResponse | null> {
  try {
    const params = new URLSearchParams();
    if (month) params.set('month', String(month));
    if (year) params.set('year', String(year));
    const qs = params.toString();
    const url = qs
      ? `${API_ENDPOINTS.HIJRI.CALENDAR}?${qs}`
      : API_ENDPOINTS.HIJRI.CALENDAR;
    return await apiService.get<HijriCalendarResponse>(url);
  } catch (error) {
    console.error('Failed to fetch hijri calendar', error);
    return null;
  }
}

/** Get user's hijri adjustment preference */
export async function getHijriPreference(): Promise<HijriPreference | null> {
  try {
    return await apiService.get<HijriPreference>(API_ENDPOINTS.HIJRI.PREFERENCES);
  } catch (error) {
    console.error('Failed to fetch hijri preference', error);
    return null;
  }
}

/** Save user's hijri adjustment preference */
export async function saveHijriPreference(
  adjustment: -1 | 0,
  city: string,
  country: string,
): Promise<HijriPreference | null> {
  try {
    return await apiService.put<HijriPreference>(
      API_ENDPOINTS.HIJRI.PREFERENCES,
      { adjustment, city, country },
    );
  } catch (error) {
    console.error('Failed to save hijri preference', error);
    return null;
  }
}
